import React, { useRef, useState } from 'react';
import { Download, FileText, Trash2, Upload } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { SkeletonText } from '../../../shared/components/Skeleton';
import { openAuthenticatedFile } from '../../../shared/lib/files';
import { useAnexosContrato, useRemoverAnexo, useUploadAnexo } from '../hooks';

interface Props {
  contractId: string;
  canEdit: boolean;
}

const DOCUMENT_TYPES = [
  { value: 'contrato_assinado', label: 'Contrato assinado' },
  { value: 'proposta', label: 'Proposta' },
  { value: 'cronograma', label: 'Cronograma' },
  { value: 'sla', label: 'SLA' },
  { value: 'termo_confidencialidade', label: 'Termo de confidencialidade' },
  { value: 'matriz_responsabilidade', label: 'Matriz de responsabilidade' },
  { value: 'outro', label: 'Outro' },
];

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Documentos/anexos do contrato (RN-COM-011). */
export function ContractAttachmentsSection({ contractId, canEdit }: Props) {
  const { data: attachments = [], isLoading } = useAnexosContrato(contractId);
  const { mutate: upload, isPending: isUploading } = useUploadAnexo();
  const { mutate: remove } = useRemoverAnexo();
  const [documentType, setDocumentType] = useState(DOCUMENT_TYPES[0].value);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    upload({ id: contractId, documentType, file });
    e.target.value = '';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900">Documentos</h2>
        {canEdit && (
          <div className="flex items-center gap-2">
            <div className="w-56">
              <AutocompleteSelect label="Tipo de documento" options={DOCUMENT_TYPES} value={documentType} onChange={setDocumentType} />
            </div>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange} />
            <Button
              variant="secondary"
              size="sm"
              loading={isUploading}
              icon={<Upload className="w-3.5 h-3.5" />}
              onClick={() => fileInputRef.current?.click()}
            >
              Enviar
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <SkeletonText lines={3} />
      ) : attachments.length === 0 ? (
        <EmptyState size="compact" icon={<FileText className="h-8 w-8" />} title="Nenhum documento anexado ainda" />
      ) : (
        <ul className="divide-y divide-gray-100 text-sm">
          {attachments.map((a) => (
            <li key={a.id} className="py-2 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-gray-800 truncate">{a.name}</p>
                  <p className="text-xs text-gray-500">
                    {DOCUMENT_TYPES.find((t) => t.value === a.documentType)?.label ?? a.documentType} · {formatBytes(a.fileSize)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => openAuthenticatedFile(`/api/contracts/${contractId}/attachments/${a.id}/download`)}
                  className="text-gray-400 hover:text-blue-600"
                  title="Baixar"
                >
                  <Download className="w-4 h-4" />
                </button>
                {canEdit && (
                  <button
                    onClick={() => remove({ id: contractId, attachmentId: a.id })}
                    className="text-gray-400 hover:text-red-600"
                    title="Remover"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
